import { useState } from 'react';
import { useParams, Link } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Loader2, Upload, Image, Pencil, Trash2 } from 'lucide-react';

import AssetUploadDialog from '@/components/AssetUploadDialog';
import DeleteAssetDialog from '@/components/DeleteAssetDialog';
import { SpeciesMonthChart } from '@/components/SpeciesMonthChart';
import { SpeciesYearlyChart } from '@/components/SpeciesYearlyChart';
import { SpeciesRecordsList } from '@/components/SpeciesRecordsList';
import { useOrgContext } from '@/contexts/OrgContext';
import { PageTopNav } from '@/components/nav/PageTopNav';

interface Record {
  id: string;
  speciesId: string;
  englishName: string;
  scientificName: string;
  dtm: string;
  count: number;
  lat: number | null;
  lng: number | null;
  note: string;
  locationNote: string;
  createdAt: string;
  updatedAt: string;
}

interface Asset {
  presignedUrl: string;
  id: string;
  recordId: string;
  type: string;
  mimeType: string;
  fileKey: string;
  originalFileName: string;
  url: string;
  uploadComplete: boolean;
  deleted: boolean;
  assetBy: string;
  note: string;
  size: number;
  createdAt: string;
  updatedAt: string;
}

const RecordDetail = () => {
  const { recordId, orgId } = useParams<{ recordId: string; orgId: string }>();
  const { orgRole } = useOrgContext();
  const queryClient = useQueryClient();
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [deleteAssetId, setDeleteAssetId] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const { data: record, isLoading: recordLoading, error: recordError } = useQuery({
    queryKey: ['record', recordId],
    queryFn: async () => {
      const response = await fetch(`/api/record/${recordId}`);
      if (!response.ok) throw new Error('Failed to fetch record details');
      return response.json() as Promise<Record>;
    },
    enabled: !!recordId,
  });

  const { data: assets, isLoading: assetsLoading, error: assetsError } = useQuery({
    queryKey: ['recordAssets', recordId],
    queryFn: async () => {
      const response = await fetch(`/api/record/${recordId}/assets`);
      if (!response.ok) throw new Error('Failed to fetch record assets');
      return response.json() as Promise<Asset[]>;
    },
    enabled: !!recordId,
  });

  if (recordLoading) {
    return (
      <div className="container mx-auto p-6 flex justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
      </div>
    );
  }

  if (recordError || !record) {
    return (
      <div className="container mx-auto p-6">
        <div className="bg-red-50 p-4 rounded-md text-red-800">
          Failed to load record. Please try again.
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto">
      <PageTopNav
        pageName="Record Details"
        rightItem={
          orgRole === 'ADMIN' ? (
            <Link to={`/app/org/${orgId}/record/${recordId}/edit`}>
              <Button>
                <Pencil className="h-4 w-4" />
                <span className="hidden md:block">Edit</span>
              </Button>
            </Link>
          ) : null
        }
      />

      <div className="flex flex-row flex-wrap gap-4">
        {/* Record Details */}
        <div className="basis-full lg:basis-1/2">
          <div>
            <h2 className="text-xl font-bold">Record Information</h2>
            <p className="text-muted-foreground">View and edit record details</p>
          </div>
          <div className="mt-4 space-y-4">
            <div>
              <h3 className="text-sm text-muted-foreground font-medium">Species</h3>
              <p>{record.englishName} ({record.scientificName})</p>
            </div>
            <div>
              <h3 className="text-sm text-muted-foreground font-medium">Date & Time</h3>
              <p>{new Date(record.dtm).toLocaleString()}</p>
            </div>
            <div>
              <h3 className="text-sm text-muted-foreground font-medium">Count</h3>
              <p>{record.count}</p>
            </div>
            {record.lat !== null && record.lng !== null && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h3 className="text-sm text-muted-foreground font-medium">Latitude</h3>
                  <p>{record.lat}</p>
                </div>
                <div>
                  <h3 className="text-sm text-muted-foreground font-medium">Longitude</h3>
                  <p>{record.lng}</p>
                </div>
              </div>
            )}
            {record.locationNote && (
              <div>
                <h3 className="text-sm text-muted-foreground font-medium">Location Notes</h3>
                <p>{record.locationNote}</p>
              </div>
            )}
            {record.note && (
              <div>
                <h3 className="text-sm text-muted-foreground font-medium">Observation Notes</h3>
                <p>{record.note}</p>
              </div>
            )}
          </div>
        </div>

        {/* Assets */}
        <div className="basis-full lg:basis-1/2">
          <div className="flex flex-row items-center justify-between">
            <h3 className="text-lg font-semibold">Assets</h3>
            <Button variant="outline" onClick={() => setUploadDialogOpen(true)}>
              <Upload className="h-4 w-4" />
              <span className="hidden md:block">Upload Asset</span>
            </Button>
          </div>
          <div className="mt-4">
            {assetsLoading ? (
              <div className="flex justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : assetsError ? (
              <div className="bg-red-50 p-4 rounded-md text-red-800">
                Failed to load assets. Please try again.
              </div>
            ) : assets && assets.length > 0 ? (
              <div className="grid grid-cols-2 gap-4">
                {assets.map((asset) => (
                  <div key={asset.id} className="border rounded-lg size-36 overflow-hidden">
                    <div className="relative aspect-square bg-muted">
                      {asset.mimeType.startsWith('image/') ? (
                        <img
                          src={asset.presignedUrl}
                          alt={`Asset ${asset.id}`}
                          className="object-cover w-full h-full"
                        />
                      ) : (
                        <div className="flex items-center justify-center h-full">
                          <Image className="h-16 w-16 text-muted-foreground" />
                        </div>
                      )}
                    </div>
                    <div className="p-2 bg-muted/20">
                      <div className="flex justify-between items-center">
                        <div className="text-xs text-muted-foreground">
                          {new Date(asset.createdAt).toLocaleDateString()}
                        </div>
                        {asset.size && (
                          <div className="text-xs text-muted-foreground">
                            Size: {(asset.size / 1024 / 1024).toFixed(2)}MB
                          </div>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-100"
                          onClick={() => {
                            setDeleteAssetId(asset.id);
                            setDeleteDialogOpen(true);
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                      <div className="flex flex-col gap-2">
                        {asset.assetBy && (
                          <p className="text-sm mt-1 truncate" title={asset.createdAt}>
                            By{' '}
                            <span className="font-semibold" title={asset.createdAt}>
                              {asset.assetBy}
                            </span>
                          </p>
                        )}
                        {asset.note && (
                          <span className="text-xs mt-1 truncate" title={asset.note}>
                            {asset.note}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center p-8 bg-muted/50 rounded-md">
                <Image className="h-12 w-12 mx-auto mb-2 text-muted-foreground" />
                <p className="text-muted-foreground">No assets found for this record.</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Click "Upload Asset" to add images.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Species Charts */}
      <div className="flex flex-row flex-wrap gap-4 mt-6">
        <div className="basis-full lg:basis-1/2">
          <SpeciesMonthChart orgId={orgId!} speciesId={record.speciesId} />
        </div>
        <div className="basis-full lg:basis-1/2">
          <SpeciesYearlyChart orgId={orgId!} speciesId={record.speciesId} />
        </div>
      </div>

      {/* Species Records */}
      <div className="mt-6">
        <SpeciesRecordsList orgId={orgId!} speciesId={record.speciesId} />
      </div>

      <AssetUploadDialog
        recordId={recordId || ''}
        open={uploadDialogOpen}
        onOpenChange={(open) => {
          setUploadDialogOpen(open);
          if (!open && uploadDialogOpen) {
            queryClient.invalidateQueries({ queryKey: ['recordAssets', recordId] });
          }
        }}
      />

      {deleteAssetId && (
        <DeleteAssetDialog
          recordId={recordId || ''}
          assetId={deleteAssetId}
          open={deleteDialogOpen}
          onOpenChange={(open) => {
            setDeleteDialogOpen(open);
            if (!open && deleteDialogOpen) {
              setDeleteAssetId(null);
              queryClient.invalidateQueries({ queryKey: ['recordAssets', recordId] });
            }
          }}
        />
      )}
    </div>
  );
};

export default RecordDetail;
